/**
 * ComparisonDashboard - Components for topology comparison view:
 * WinnerBanner, ComparisonMetricCard, EfficiencyComparisonChart.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Bolt as BoltIcon } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { vortxTypography } from '@vortx/shared-ui';
import { COLORS, fadeInUp, pulseGlow } from './ResultsDesignTokens';

// Winner Banner with animated trophy
export const WinnerBanner = ({ winner, topologies }) => {
    const winnerTopology = topologies?.find(t => t.id === winner?.topology_id);
    const advantagePct = winner?.efficiency_advantage_pct || 0;

    return (
        <Box sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(COLORS.efficiency, 0.15)} 0%, ${alpha(COLORS.accent, 0.15)} 100%)`,
            border: `2px solid ${alpha(COLORS.efficiency, 0.4)}`,
            position: 'relative',
            overflow: 'hidden',
            animation: `${fadeInUp} 0.6s ease-out`,
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg, ${COLORS.efficiency}, ${COLORS.accent}, ${COLORS.efficiency})`,
                boxShadow: `0 0 20px ${alpha(COLORS.efficiency, 0.6)}`,
            },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: alpha(COLORS.efficiency, 0.2),
                        border: `2px solid ${COLORS.efficiency}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: `${pulseGlow} 2s ease-in-out infinite`,
                        color: COLORS.efficiency,
                    }}>
                        <BoltIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: COLORS.text.muted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                        }}>
                            Most Efficient Design
                        </Typography>
                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: COLORS.efficiency,
                            textShadow: `0 0 20px ${alpha(COLORS.efficiency, 0.5)}`,
                        }}>
                            Topology #{winner?.topology_id}: {winnerTopology?.name || 'Unknown'}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{
                    p: 2,
                    px: 3,
                    bgcolor: alpha(COLORS.efficiency, 0.15),
                    borderRadius: 2,
                    border: `1px solid ${alpha(COLORS.efficiency, 0.3)}`,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: COLORS.text.muted,
                        textAlign: 'center',
                    }}>
                        EFFICIENCY ADVANTAGE
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontSize: '2rem',
                        fontWeight: 800,
                        color: COLORS.efficiency,
                        textShadow: `0 0 25px ${alpha(COLORS.efficiency, 0.6)}`,
                        textAlign: 'center',
                    }}>
                        +{advantagePct.toFixed(2)}%
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

// Comparison metric card - shows both values side by side
export const ComparisonMetricCard = ({ icon: Icon, label, value1, value2, unit, color, topology1Name, topology2Name, winnerIndex }) => (
    <Box sx={{
        p: 2,
        bgcolor: COLORS.bg.card,
        borderRadius: 2,
        border: `1px solid ${alpha(color, 0.2)}`,
        animation: `${fadeInUp} 0.5s ease-out`,
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Icon sx={{ color, fontSize: 18 }} />
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.7rem',
                color: COLORS.text.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
            }}>
                {label}
            </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
            {[{ value: value1, name: topology1Name, idx: 0 }, { value: value2, name: topology2Name, idx: 1 }].map((item, i) => (
                <Box key={i} sx={{
                    flex: 1,
                    p: 1.5,
                    bgcolor: winnerIndex === item.idx ? alpha(COLORS.efficiency, 0.1) : 'rgba(255,255,255,0.02)',
                    borderRadius: 1,
                    border: `1px solid ${winnerIndex === item.idx ? alpha(COLORS.efficiency, 0.3) : 'rgba(255,255,255,0.05)'}`,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.6rem',
                        color: COLORS.text.muted,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        mb: 0.5,
                    }}>
                        {item.name}
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: winnerIndex === item.idx ? COLORS.efficiency : COLORS.text.primary,
                    }}>
                        {typeof item.value === 'number' ? item.value.toLocaleString() : '--'}
                        <Typography component="span" sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.6rem',
                            color: COLORS.text.secondary,
                            ml: 0.5,
                        }}>
                            {unit}
                        </Typography>
                    </Typography>
                </Box>
            ))}
        </Box>
    </Box>
);

// Efficiency comparison bar chart
export const EfficiencyComparisonChart = ({ results, topologies }) => {
    // API returns { results: [...], recommendation, efficiency_gain_pct }
    const resultsArray = results?.results || [];

    // Map results to topologies in the same order as selectedTopologies
    const getResultForTopology = (topoId) => resultsArray.find(r => r.topology_id === topoId);

    const data = [
        {
            name: `#${topologies[0]?.id || 1}`,
            fullName: topologies[0]?.name || getResultForTopology(topologies[0]?.id)?.topology_name || 'Topology 1',
            efficiency: getResultForTopology(topologies[0]?.id)?.efficiency_pct || 0,
        },
        {
            name: `#${topologies[1]?.id || 2}`,
            fullName: topologies[1]?.name || getResultForTopology(topologies[1]?.id)?.topology_name || 'Topology 2',
            efficiency: getResultForTopology(topologies[1]?.id)?.efficiency_pct || 0,
        },
    ];

    // Determine winner
    const winnerIndex = data[0].efficiency > data[1].efficiency ? 0 : 1;

    return (
        <Box sx={{
            p: 3,
            bgcolor: COLORS.bg.card,
            borderRadius: 2,
            border: `1px solid ${alpha(COLORS.accent, 0.2)}`,
            animation: `${fadeInUp} 0.5s ease-out 0.2s both`,
        }}>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
                color: COLORS.text.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                mb: 2,
            }}>
                Efficiency Comparison
            </Typography>
            <Box sx={{ height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                        <defs>
                            <linearGradient id="gradBarWinner" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor={COLORS.efficiency} stopOpacity={0.9} />
                                <stop offset="100%" stopColor={COLORS.efficiency} stopOpacity={0.4} />
                            </linearGradient>
                            <linearGradient id="gradBarLoser" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor={COLORS.accent} stopOpacity={0.7} />
                                <stop offset="100%" stopColor={COLORS.accent} stopOpacity={0.3} />
                            </linearGradient>
                        </defs>
                        <XAxis
                            type="number"
                            domain={[0, 100]}
                            tick={{ fill: COLORS.text.muted, fontSize: 11, fontFamily: vortxTypography.mono }}
                            axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                            tickFormatter={(v) => `${v}%`}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            tick={{ fill: COLORS.text.secondary, fontSize: 12, fontFamily: vortxTypography.mono, fontWeight: 600 }}
                            axisLine={false}
                            tickLine={false}
                            width={60}
                        />
                        <Tooltip
                            content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0].payload;
                                const isWinner = data.indexOf(d) === winnerIndex;
                                return (
                                    <Box sx={{
                                        bgcolor: 'rgba(8, 8, 12, 0.95)',
                                        border: `1px solid ${alpha(isWinner ? COLORS.efficiency : COLORS.accent, 0.4)}`,
                                        borderRadius: 1,
                                        p: 1.5,
                                    }}>
                                        <Typography sx={{
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.8rem',
                                            color: COLORS.text.primary,
                                            fontWeight: 600,
                                        }}>
                                            {d.fullName}
                                        </Typography>
                                        <Typography sx={{
                                            fontFamily: vortxTypography.heading,
                                            fontSize: '1.25rem',
                                            fontWeight: 700,
                                            color: isWinner ? COLORS.efficiency : COLORS.accent,
                                        }}>
                                            {d.efficiency.toFixed(2)}% Efficient
                                        </Typography>
                                    </Box>
                                );
                            }}
                        />
                        <Bar dataKey="efficiency" radius={[0, 4, 4, 0]}>
                            {data.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={index === winnerIndex ? 'url(#gradBarWinner)' : 'url(#gradBarLoser)'}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 2 }}>
                {data.map((d, i) => (
                    <Box key={i} sx={{ textAlign: 'center' }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.65rem',
                            color: COLORS.text.muted,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 150,
                        }}>
                            {d.fullName}
                        </Typography>
                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontSize: '1.75rem',
                            fontWeight: 800,
                            color: i === winnerIndex ? COLORS.efficiency : COLORS.accent,
                            textShadow: i === winnerIndex ? `0 0 20px ${alpha(COLORS.efficiency, 0.5)}` : 'none',
                        }}>
                            {d.efficiency.toFixed(1)}%
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
